import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BarChart3, Building2, FileText, LockKeyhole, ReceiptText, ShieldCheck } from 'lucide-react';

const Welcome: React.FC = () => {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen overflow-hidden bg-slate-950 text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(37,99,235,0.35),transparent_30%),radial-gradient(circle_at_85%_85%,rgba(20,184,166,0.22),transparent_26%)]" />
      <div className="relative mx-auto flex min-h-screen max-w-7xl flex-col px-6 py-6 lg:px-10">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-primary-700 shadow-lg shadow-primary-950/30">
              <Building2 size={23} />
            </div>
            <div>
              <p className="text-sm font-semibold tracking-wide text-white">LevyLedger</p>
              <p className="text-xs text-slate-400">Body Corporate Management</p>
            </div>
          </div>
          <button onClick={() => navigate('/login')} className="rounded-xl border border-white/20 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10">
            Sign in
          </button>
        </header>

        <section className="flex flex-1 flex-col justify-center py-16 lg:py-20">
          <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="max-w-2xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-teal-300/20 bg-teal-300/10 px-3 py-1 text-sm font-medium text-teal-100">
                <ShieldCheck size={16} /> Clear accounts. Confident decisions.
              </div>
              <h1 className="text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                Levy management that feels beautifully simple.
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-slate-300">
                Keep every unit, owner, levy and payment in one secure ledger. Create clear professional statements in minutes, not spreadsheets.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <button onClick={() => navigate('/login')} className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 font-semibold text-slate-900 shadow-xl shadow-black/20 transition hover:-translate-y-0.5 hover:bg-slate-100">
                  Access your workspace <ArrowRight size={18} />
                </button>
                <div className="inline-flex items-center justify-center gap-2 px-4 py-3 text-sm text-slate-300">
                  <LockKeyhole size={16} className="text-teal-300" /> Secure staff access
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-white/15 bg-white/10 p-4 shadow-2xl shadow-black/30 backdrop-blur-md sm:p-6">
              <div className="rounded-2xl bg-slate-50 p-5 text-slate-900 shadow-inner sm:p-6">
                <div className="flex items-start justify-between border-b border-slate-200 pb-5">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Scheme overview</p>
                    <p className="mt-1 text-2xl font-bold">Your accounts, at a glance</p>
                  </div>
                  <div className="rounded-xl bg-primary-100 p-3 text-primary-700"><BarChart3 size={23} /></div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-100"><p className="text-xs text-slate-500">Accounts</p><p className="mt-1 text-2xl font-bold">All units</p></div>
                  <div className="rounded-xl bg-teal-50 p-4 shadow-sm ring-1 ring-teal-100"><p className="text-xs text-teal-700">Payments</p><p className="mt-1 text-2xl font-bold text-teal-800">Tracked</p></div>
                </div>
                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-3"><div className="rounded-lg bg-amber-100 p-2 text-amber-700"><ReceiptText size={18} /></div><div><p className="font-semibold">Accurate ledger balances</p><p className="text-sm text-slate-500">Charges, payments and corrections preserved.</p></div></div>
                </div>
                <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-3"><div className="rounded-lg bg-indigo-100 p-2 text-indigo-700"><FileText size={18} /></div><div><p className="font-semibold">Ready-to-send statements</p><p className="text-sm text-slate-500">Professional PDF statements with your branding.</p></div></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer className="flex flex-col gap-2 border-t border-white/10 pt-5 text-xs text-slate-400 sm:flex-row sm:items-center sm:justify-between">
          <span>Built for Body Corporates and managing agents.</span>
          <span>Private ledger • Professional statements • Audit history</span>
        </footer>
      </div>
    </main>
  );
};

export default Welcome;
